import { CombinedGraphQLErrors } from '@apollo/client'
import { ErrorAlert } from '@repo/shared-client/components/blocks'

import { Button } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { captureException as sentryCaptureException } from '@sentry/tanstackstart-react'
import { useNavigate } from '@tanstack/react-router'
import { isDevelopmentEnvironment } from '@web/src/lib/app-environment'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { GraphQLError } from 'graphql/error'

const SUPPORT_EMAIL = 'support@spicyfriends.com'

interface GlobalErrorHandlerProps {
  error?: Error
  reset?: () => void
}

const GlobalErrorHandler = ({ error, reset }: GlobalErrorHandlerProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  if (getAppConfig().SENTRY_DSN_WEB && !getAppConfig().SENTRY_DISABLE) {
    sentryCaptureException(error)
  }

  const isGraphQlError =
    (error != null && CombinedGraphQLErrors.is(error)) ||
    error instanceof CombinedGraphQLErrors ||
    error instanceof GraphQLError ||
    error?.name === 'CombinedGraphQLErrors' ||
    error?.name === 'GraphQLError'

  const handleGoHome = () => {
    navigate({ to: '/' })
  }

  const handleRefresh = () => {
    if (reset) {
      reset()
    } else {
      window.location.reload()
    }
  }

  return (
    <div className={cn('min-h-screen bg-background')}>
      <div className={cn('mx-auto w-full max-w-4xl px-4 py-8')}>
        {isGraphQlError ? (
          <ErrorAlert error={error} />
        ) : (
          <div className={cn('flex flex-col gap-6')}>
            <div className={cn('flex flex-col gap-4')}>
              <h1 className={cn('font-semibold text-3xl text-destructive tracking-tight')}>
                {t('Oops! Something went wrong')}
              </h1>
              <p className={cn('text-lg text-muted-foreground')}>
                {t(
                  "We're sorry, but an unexpected error occurred. Please try refreshing the page or return to the home page."
                )}
              </p>
            </div>

            {error ? (
              <div className={cn('rounded-md border border-border bg-muted p-4 ring-1 ring-border/60')}>
                <div className={cn('flex flex-col gap-2')}>
                  <p className={cn('font-semibold text-muted-foreground text-sm')}>{t('Error Details:')}</p>
                  <pre
                    className={cn(
                      'overflow-x-auto whitespace-pre-wrap break-words rounded-sm bg-background p-3 text-foreground text-sm'
                    )}
                  >
                    {error.message || t('An unknown error occurred')}
                  </pre>
                  {error.stack && isDevelopmentEnvironment() ? (
                    <details className={cn('mt-2 rounded-sm bg-background p-3 text-xs')}>
                      <summary
                        className={cn(
                          'mb-2 cursor-pointer font-semibold text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
                        )}
                      >
                        {t('Stack Trace')}
                      </summary>
                      <pre
                        className={cn('overflow-x-auto whitespace-pre-wrap break-words text-muted-foreground text-xs')}
                      >
                        {error.stack}
                      </pre>
                    </details>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className={cn('flex flex-wrap items-center gap-4')}>
              <Button onClick={handleRefresh} size="lg" type="button" variant="default">
                {t('Refresh Page')}
              </Button>
              <Button onClick={handleGoHome} size="lg" type="button" variant="outline">
                {t('Go to Home')}
              </Button>
            </div>

            <div className={cn('mt-4')}>
              <p className={cn('text-muted-foreground text-sm')}>
                {t('If this issue persists, please contact')}{' '}
                <a
                  className={cn(
                    'text-primary underline underline-offset-4 hover:text-primary/90 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
                  )}
                  href={`mailto:${SUPPORT_EMAIL}`}
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GlobalErrorHandler
